import { Suspense } from 'react';
import { Link, useHistory, useRouteMatch } from 'react-router-dom';
import { BoxIcon } from '@givebutter/primitives/box-icon';
import { Button } from '@givebutter/primitives/button';
import { Separator } from '@givebutter/primitives/separator';
import {
    faChevronDown,
    faCircleInfo as faCircleInfoRegular,
    faEllipsisV,
    faPen,
    faSave,
} from '@fortawesome/pro-regular-svg-icons';
import { faCircleInfo as faCircleInfoSolid } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Content from 'assets/js/components/Content';
import * as DeleteReportDialog from 'assets/js/pages/reports/DeleteReportDialog';
import * as ExportReportDialog from 'assets/js/pages/reports/ExportReportDialog';
import { ReportChart } from 'assets/js/pages/reports/ReportChart';
import { ReportDetailProvider } from 'assets/js/pages/reports/ReportDetailProvider';
import { ReportFilterBar } from 'assets/js/pages/reports/ReportFilterBar';
import { ReportTableStrategy } from 'assets/js/pages/reports/ReportTableStrategy';
import { ResetReportStateDropdownItem } from 'assets/js/pages/reports/ResetReportStateDropdownItem';
import * as SaveAsNewReport from 'assets/js/pages/reports/SaveAsNewReportDialog';
import { UpdateCurrentReportButton } from 'assets/js/pages/reports/UpdateCurrentReportButton';
import { useQueryDiff } from 'assets/js/pages/reports/useQueryDiff';
import * as Breadcrumbs from 'components/breadcrumbs';
import * as Dialog from 'components/dialog';
import * as DropdownMenu from 'components/dropdown-menu';
import { transformPartitionedSavedFilters } from 'components/filter/filters-next/downstream';
import { useFiltersByKey } from 'components/filter/filters-next/useFiltersByKey';
import { useInitFilters } from 'components/filter/filters-next/useInitFilters';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentReport } from 'customHooks/useCurrentReport';

const ReportDetail = () => {
    const { accountId } = useCurrentAccount();
    const history = useHistory();
    const match = useRouteMatch();
    const { data: report } = useCurrentReport();
    const hasDiff = useQueryDiff();
    const isDescriptionEmpty = !report.description || report.description.length === 0;
    const isTemplate = report.is_default_template;

    return (
        <Content>
            <div className={'flex flex-col gap-4'}>
                <div className={'flex flex-col gap-2'}>
                    <div className={'flex flex-wrap items-start justify-between gap-4'}>
                        <div className={'flex flex-col gap-2'}>
                            <Breadcrumbs.Root>
                                <Breadcrumbs.Crumb to={`/accounts/${accountId}/reports`}>
                                    Reports
                                </Breadcrumbs.Crumb>
                                <Breadcrumbs.Crumb
                                    to={`/accounts/${accountId}/reports/${report.id}`}
                                >
                                    {report.name}
                                </Breadcrumbs.Crumb>
                            </Breadcrumbs.Root>
                            <div className={'flex items-center gap-2'}>
                                <p className={'text-2xl font-medium leading-none'}>{report.name}</p>
                                {!isTemplate && (
                                    <Link
                                        to={`${match.url}/edit`}
                                        className={'flex size-4 items-center justify-center'}
                                    >
                                        <FontAwesomeIcon className={'text-blue-500'} icon={faPen} />
                                    </Link>
                                )}
                                {isTemplate && (
                                    <Dialog.Root>
                                        <Dialog.Trigger
                                            className={'flex size-4 items-center justify-center'}
                                        >
                                            <FontAwesomeIcon
                                                className={'text-blue-500'}
                                                icon={faCircleInfoSolid}
                                            />
                                        </Dialog.Trigger>
                                        <Dialog.Portal>
                                            <Dialog.Overlay />
                                            <Dialog.Content>
                                                <Dialog.Close />
                                                <div className={'flex flex-col gap-6'}>
                                                    <BoxIcon size="lg">
                                                        <FontAwesomeIcon
                                                            size={'xl'}
                                                            icon={faCircleInfoRegular}
                                                        />
                                                    </BoxIcon>
                                                    <div className={'flex flex-col gap-3'}>
                                                        <p
                                                            className={
                                                                'text-xl font-medium text-gray-900'
                                                            }
                                                        >
                                                            About this report
                                                        </p>
                                                        <p className={'text-sm text-gray-600'}>
                                                            {report.description}
                                                        </p>
                                                    </div>
                                                    <Dialog.Cancel asChild={true}>
                                                        <Button
                                                            variant={'outline'}
                                                            color={'neutral'}
                                                        >
                                                            Close
                                                        </Button>
                                                    </Dialog.Cancel>
                                                </div>
                                            </Dialog.Content>
                                        </Dialog.Portal>
                                    </Dialog.Root>
                                )}
                            </div>
                            {!isTemplate && isDescriptionEmpty && (
                                <Link to={`${match.url}/edit`} className={'text-blue-500'}>
                                    Add description +
                                </Link>
                            )}
                        </div>
                        <div key={report.id} className={'flex flex-wrap items-center gap-2'}>
                            {!isTemplate && <UpdateCurrentReportButton />}
                            {isTemplate && hasDiff && (
                                <SaveAsNewReport.Root>
                                    <SaveAsNewReport.Trigger asChild={true}>
                                        <Button
                                            variant={'outline'}
                                            color={'neutral'}
                                            onSelect={e => e.preventDefault()}
                                        >
                                            <FontAwesomeIcon icon={faSave} />
                                            Save as new
                                        </Button>
                                    </SaveAsNewReport.Trigger>
                                </SaveAsNewReport.Root>
                            )}
                            <DropdownMenu.Root>
                                <DropdownMenu.Trigger asChild={true}>
                                    <Button variant="solid">
                                        Share
                                        <FontAwesomeIcon icon={faChevronDown} />
                                    </Button>
                                </DropdownMenu.Trigger>
                                <DropdownMenu.Content align={'end'} sideOffset={6}>
                                    <DropdownMenu.LinkItem
                                        to={`/accounts/${accountId}/reports/${report.id}/schedules/new`}
                                    >
                                        Share via email
                                    </DropdownMenu.LinkItem>
                                    <ExportReportDialog.Root>
                                        <ExportReportDialog.Trigger asChild={true}>
                                            <DropdownMenu.Item onSelect={e => e.preventDefault()}>
                                                Export CSV
                                            </DropdownMenu.Item>
                                        </ExportReportDialog.Trigger>
                                    </ExportReportDialog.Root>
                                    <DropdownMenu.LinkItem
                                        disabled={report.schedules_count === 0}
                                        to={`/accounts/${accountId}/reports/${report.id}/schedules`}
                                    >
                                        Manage shared emails
                                    </DropdownMenu.LinkItem>
                                </DropdownMenu.Content>
                            </DropdownMenu.Root>
                            <DropdownMenu.Root>
                                <DropdownMenu.Trigger asChild={true}>
                                    <Button
                                        variant={'outline'}
                                        color={'neutral'}
                                        shape={'square'}
                                        aria-label="More"
                                    >
                                        <FontAwesomeIcon icon={faEllipsisV} />
                                    </Button>
                                </DropdownMenu.Trigger>
                                <DropdownMenu.Content align={'end'} sideOffset={4}>
                                    {!isTemplate && (
                                        <SaveAsNewReport.Root>
                                            <SaveAsNewReport.Trigger asChild={true}>
                                                <DropdownMenu.Item
                                                    onSelect={e => e.preventDefault()}
                                                >
                                                    Save as new
                                                </DropdownMenu.Item>
                                            </SaveAsNewReport.Trigger>
                                        </SaveAsNewReport.Root>
                                    )}
                                    <ResetReportStateDropdownItem />
                                    {!isTemplate && (
                                        <DeleteReportDialog.Root
                                            reportId={report.id}
                                            onSuccess={() =>
                                                history.push(`/accounts/${accountId}/reports`)
                                            }
                                        >
                                            <DeleteReportDialog.Trigger asChild={true}>
                                                <DropdownMenu.Item
                                                    onSelect={e => e.preventDefault()}
                                                >
                                                    Delete report
                                                </DropdownMenu.Item>
                                            </DeleteReportDialog.Trigger>
                                        </DeleteReportDialog.Root>
                                    )}
                                </DropdownMenu.Content>
                            </DropdownMenu.Root>
                        </div>
                    </div>
                    {!isTemplate && !isDescriptionEmpty && (
                        <p className={'text-gray-700'}>{report.description}</p>
                    )}
                </div>
                <Separator />
                <ReportFilterBar />
                <Suspense>
                    <ReportChart />
                </Suspense>
                <ReportTableStrategy />
            </div>
        </Content>
    );
};

const InitFiltersWrapper = () => {
    const { data: report } = useCurrentReport();
    const { didInit } = useFiltersByKey('singleReport');
    useInitFilters('singleReport', transformPartitionedSavedFilters(report.query.filters));

    if (didInit) {
        return (
            <ReportDetailProvider>
                <ReportDetail />
            </ReportDetailProvider>
        );
    }

    return null;
};

export { InitFiltersWrapper as ReportDetail };
